import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import {
  Autocomplete,
  Box,
  FormHelperText,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import axiosInstance from 'src/utils/axios';
import { fDate } from 'src/utils/format-time';

import { useAppSelector } from 'src/redux/store';
import { TCreateStationeryReturn } from 'src/redux/slices/stationery-return';

import Iconify from 'src/components/iconify';
import { RHFSwitch, RHFTextField } from 'src/components/hook-form';

import { IEmployeeListItem } from 'src/types/employee';
import { IStationeryDispatchItemSearchResult } from 'src/types/stationery-dispatch';

interface Props {
  index: number;
  remove: (index: number) => void;
  isLast: boolean;
  // Seeds the ad-hoc search results with the line's already-selected
  // dispatch item (edit mode only) — otherwise the Autocomplete has no way
  // to resolve a display name for it before the user types a search query.
  initialDispatchItem?: IStationeryDispatchItemSearchResult;
  // The return being edited, if any — excluded from the "already returned"
  // sum so this line's own current contribution doesn't count against
  // itself when computing the outstanding cap.
  excludeReturnId?: string;
  // The damaged-quantity split can only ever be set when creating a return
  // — a return with damaged lines can't be edited at all (see
  // stationery-return-new-edit-form.tsx), so this toggle/split UI is hidden
  // entirely in edit mode rather than shown and disabled.
  allowDamaged?: boolean;
}

interface OutstandingState {
  status: 'idle' | 'loading' | 'success' | 'failure';
  dispatchedQuantity: number;
  returnedQuantity: number;
  outstandingQuantity: number;
}

const initialOutstanding: OutstandingState = {
  status: 'idle',
  dispatchedQuantity: 0,
  returnedQuantity: 0,
  outstandingQuantity: 0,
};

export default function StationeryReturnNewItemRow({
  index,
  remove,
  isLast,
  initialDispatchItem,
  excludeReturnId,
  allowDamaged = false,
}: Props) {
  const [dispatchSearchQuery, setDispatchSearchQuery] = useState('');
  const [localDispatchItems, setLocalDispatchItems] = useState<
    IStationeryDispatchItemSearchResult[]
  >(initialDispatchItem ? [initialDispatchItem] : []);
  const [outstanding, setOutstanding] = useState<OutstandingState>(initialOutstanding);

  const { employees } = useAppSelector((state) => state.employees);

  const {
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<TCreateStationeryReturn>();

  const defaultHelperText: string = errors.items?.[index] ? ' ' : '';

  const sourceDispatchItemId = useWatch({
    control,
    name: `items.${index}.sourceDispatchItem` as any,
  });

  const quantity = useWatch({ control, name: `items.${index}.quantity` as any });
  const damaged = useWatch({ control, name: `items.${index}.damaged` as any });
  const supplierDamagedQuantity = useWatch({
    control,
    name: `items.${index}.supplierDamagedQuantity` as any,
  });
  const staffDamagedQuantity = useWatch({
    control,
    name: `items.${index}.staffDamagedQuantity` as any,
  });
  const totalDamagedQuantity =
    (Number(supplierDamagedQuantity) || 0) + (Number(staffDamagedQuantity) || 0);

  // Clears any stale split values whenever the toggle is off (including on
  // mount) so a turned-off line never silently submits leftover quantities.
  useEffect(() => {
    if (!damaged) {
      setValue(`items.${index}.supplierDamagedQuantity` as any, 0);
      setValue(`items.${index}.staffDamagedQuantity` as any, 0);
    }
  }, [damaged, index, setValue]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchPaginatedDispatchItems = useCallback(
    debounce(async (query) => {
      try {
        const response = await axiosInstance.get(
          `stationery/dispatch/item?page=1&limit=25&generalSearch=${query}&sortBy=dispatch.dispatchDate&sort=desc`
        );

        setLocalDispatchItems(response.data.data.items);
      } catch (err) {
        console.warn(err);
      }
    }, 500),
    []
  );

  useEffect(() => {
    fetchPaginatedDispatchItems(dispatchSearchQuery);

    return fetchPaginatedDispatchItems.cancel;
  }, [dispatchSearchQuery, fetchPaginatedDispatchItems]);

  useEffect(() => {
    let active = true;

    if (!sourceDispatchItemId) {
      setOutstanding(initialOutstanding);
      return undefined;
    }

    setOutstanding((prev) => ({ ...prev, status: 'loading' }));

    axiosInstance
      .get(
        `stationery/dispatch/item/${sourceDispatchItemId}/outstanding?excludeReturnId=${
          excludeReturnId ?? ''
        }`
      )
      .then((response) => {
        if (active) setOutstanding({ ...response.data.data, status: 'success' });
      })
      .catch(() => {
        if (active) setOutstanding((prev) => ({ ...prev, status: 'failure' }));
      });

    return () => {
      active = false;
    };
  }, [sourceDispatchItemId, excludeReturnId]);

  // Whoever received an item is very often the one returning it — default
  // this line's issuedBy from the picked dispatch line's own receivedBy,
  // but only if the user hasn't already set (or carried over) a value.
  useEffect(() => {
    if (!sourceDispatchItemId) return;

    const currentIssuedBy = getValues(`items.${index}.issuedBy` as any);
    if (currentIssuedBy) return;

    const matchedDispatchItem = localDispatchItems.find(
      (item) => item._id === sourceDispatchItemId
    );
    if (matchedDispatchItem?.receivedBy) {
      setValue(`items.${index}.issuedBy` as any, matchedDispatchItem.receivedBy._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceDispatchItemId]);

  const outstandingHelperText =
    outstanding.status === 'success'
      ? `Dispatched: ${outstanding.dispatchedQuantity} — Returned: ${outstanding.returnedQuantity} — Outstanding: ${outstanding.outstandingQuantity}`
      : defaultHelperText;

  return (
    <TableRow>
      <TableCell>
        {index + 1 < 10 ? `0${index + 1}` : index + 1}

        <FormHelperText>{defaultHelperText}</FormHelperText>
      </TableCell>

      <TableCell sx={{ minWidth: 340 }}>
        <Controller
          name={`items.${index}.sourceDispatchItem`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Autocomplete
              {...field}
              fullWidth
              size="small"
              multiple={false}
              options={localDispatchItems}
              isOptionEqualToValue={(
                option: IStationeryDispatchItemSearchResult | string,
                value: IStationeryDispatchItemSearchResult | string
              ) => {
                const optionId = typeof option === 'string' ? option : option?._id;
                const valueId = typeof value === 'string' ? value : value?._id;
                return optionId === valueId;
              }}
              getOptionLabel={(option: IStationeryDispatchItemSearchResult | string) => {
                if (!option) return '';
                if (typeof option === 'string') {
                  if (option.length === 0) return option;
                  const value = localDispatchItems.find(
                    (item: IStationeryDispatchItemSearchResult) => item._id === option
                  );
                  return value
                    ? `${value.dispatch.dispatchCode} — ${fDate(value.dispatch.dispatchDate)} — ${value.stationeryItem.itemName}`
                    : '';
                }
                return `${option.dispatch.dispatchCode} — ${fDate(option.dispatch.dispatchDate)} — ${option.stationeryItem.itemName}`;
              }}
              renderOption={(props, option: IStationeryDispatchItemSearchResult | string) => {
                if (typeof option === 'string') {
                  return (
                    <li {...props} key={option}>
                      {option}
                    </li>
                  );
                }
                return (
                  <li {...props} key={option._id}>
                    {option.dispatch.dispatchCode} — {fDate(option.dispatch.dispatchDate)} —{' '}
                    {option.stationeryItem.itemName} — Qty:{' '}
                    {option.quantity}
                  </li>
                );
              }}
              onInputChange={(_, value, reason) => {
                if (reason === 'input') setDispatchSearchQuery(value);
                if (reason === 'clear') setDispatchSearchQuery('');
              }}
              renderInput={(params) => (
                <TextField
                  error={!!error}
                  required
                  helperText={error ? error.message : defaultHelperText}
                  {...params}
                />
              )}
              onChange={(event, newValue) => {
                field.onChange(typeof newValue === 'string' ? newValue : newValue?._id ?? '');
              }}
            />
          )}
        />
      </TableCell>

      <TableCell sx={{ minWidth: 200 }}>
        <RHFTextField
          fullWidth
          required
          type="number"
          size="small"
          name={`items.${index}.quantity`}
          helperText={outstandingHelperText}
          showError={false}
          inputProps={
            outstanding.status === 'success'
              ? { max: outstanding.outstandingQuantity, min: 0.001, step: 'any' }
              : { min: 0.001, step: 'any' }
          }
        />
      </TableCell>

      <TableCell sx={{ minWidth: 220 }}>
        <Controller
          name={`items.${index}.issuedBy`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Autocomplete
              {...field}
              fullWidth
              size="small"
              multiple={false}
              options={employees.employees}
              isOptionEqualToValue={(
                option: IEmployeeListItem | string,
                value: IEmployeeListItem | string
              ) => {
                const optionId = typeof option === 'string' ? option : option?._id;
                const valueId = typeof value === 'string' ? value : value?._id;
                return optionId === valueId;
              }}
              getOptionLabel={(option: IEmployeeListItem | string) => {
                if (!option) return '';
                if (typeof option === 'string') {
                  if (option.length === 0) return option;
                  const value = employees.employees.find(
                    (employee: IEmployeeListItem) => employee._id === option
                  );
                  return value
                    ? `${value.firstName}${value.lastName ? ` ${value.lastName}` : ''} (${value.employeeNumber})`
                    : '';
                }
                return `${option.firstName}${option.lastName ? ` ${option.lastName}` : ''} (${option.employeeNumber})`;
              }}
              renderOption={(props, option: IEmployeeListItem | string) => {
                if (typeof option === 'string') {
                  return (
                    <li {...props} key={option}>
                      {option}
                    </li>
                  );
                }
                return (
                  <li {...props} key={option._id}>
                    {option.firstName}
                    {option.lastName ? ` ${option.lastName}` : ''} ({option.employeeNumber})
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  label="Issued By"
                  error={!!error}
                  required
                  helperText={error ? error.message : defaultHelperText}
                  {...params}
                />
              )}
              onChange={(event, newValue) => {
                field.onChange(typeof newValue === 'string' ? newValue : newValue?._id ?? '');
              }}
            />
          )}
        />
      </TableCell>

      {allowDamaged && (
        <TableCell sx={{ minWidth: 260 }}>
          <RHFSwitch name={`items.${index}.damaged`} label="Damaged" />

          {damaged && (
            <Stack spacing={1} mt={1}>
              <RHFTextField
                fullWidth
                type="number"
                size="small"
                name={`items.${index}.supplierDamagedQuantity`}
                label="Supplier Damage Qty"
                showError={false}
                inputProps={{ min: 0, step: 'any' }}
              />
              <RHFTextField
                fullWidth
                type="number"
                size="small"
                name={`items.${index}.staffDamagedQuantity`}
                label="Staff Damage Qty"
                showError={false}
                inputProps={{ min: 0, step: 'any' }}
              />
              <Box>
                <Typography
                  variant="caption"
                  color={
                    totalDamagedQuantity > 0 && totalDamagedQuantity <= (Number(quantity) || 0)
                      ? 'text.disabled'
                      : 'error.main'
                  }
                >
                  Damaged: {totalDamagedQuantity} / {quantity || 0} — Good (added to stock):{' '}
                  {Math.max((Number(quantity) || 0) - totalDamagedQuantity, 0)}
                </Typography>
              </Box>
            </Stack>
          )}
        </TableCell>
      )}

      <TableCell align="right">
        <Tooltip title={isLast ? 'Last item cannot be deleted' : 'Delete'}>
          <span>
            <IconButton disabled={isLast} color="error" onClick={() => remove(index)}>
              <Iconify icon="solar:trash-bin-trash-bold" />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}
